import { injectable } from 'inversify';
import { autorun, makeAutoObservable, reaction } from 'mobx';
import { RegistrationWizardViewModel } from '@widgets/registrationWizard/store/RegistrationWizardViewModel';
import { queryClient } from '@shared/api/reactQuery';
import { fromDocToFile, getFileErrorMessage } from '@shared/utils/files';
import { DocTypeCodes, RegistrationDocFile } from '@shared/types/registration';
import { AddScanDoc } from '@shared/types/osiAccountApplications';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { DbRegistrationDoc, ResponsesRequiredDocsResponse } from '@/shared/api/orval/models';
import {
  getDeleteApiRegistrationsIdDocsDocIdMutationOptions,
  getGetApiRegistrationsIdDocsQueryKey,
  getGetApiRegistrationsIdDocsQueryOptions,
  getGetApiRegistrationsReqdocsQueryKey,
  getGetApiRegistrationsReqdocsQueryOptions,
  getPostApiRegistrationsIdDocsMutationOptions
} from '@/shared/api/orval/registrations/registrations';

@injectable()
export class RegistrationDocsViewModel {
  wizard: RegistrationWizardViewModel | null = null;

  files: any[] = [];

  requiredDocs: ResponsesRequiredDocsResponse[] | null | undefined = null;

  isLoading = false;

  /**
   * Прогресс клиентского сжатия PDF: null — сжатие не идёт.
   * docTypeCode нужен, чтобы индикатор показывался только у той зоны загрузки, куда
   * бросили файл: одна и та же viewModel обслуживает все типы документов шага.
   */
  compressionProgress: { docTypeCode: string; page: number; total: number } | null = null;

  private _mutationCache = queryClient.getMutationCache();

  private _queryCache = queryClient.getQueryCache();

  private _disposeAllReactionsController = new AbortController();

  constructor() {
    makeAutoObservable(this);

    autorun(async () => {
      await this.loadData();
      await this.loadFiles();
    });

    reaction(
      () => this.wizard?.registration,
      async (registration, prev) => {
        if (!registration?.unionTypeId) return;
        if (registration.unionTypeId === prev?.unionTypeId) return;

        await queryClient.invalidateQueries({
          queryKey: getGetApiRegistrationsReqdocsQueryKey({ registrationId: registration.id })
        });
      },
      { signal: this._disposeAllReactionsController.signal }
    );

    // TODO: надо отписываться, но когда добавил отписку в cleanup - перестала отрабатывать эта функция
    this._queryCache.subscribe(async ({ query }) => {
      if (!this.wizard?.registration?.id) return;

      const getRegistrationDocsKey = getGetApiRegistrationsIdDocsQueryKey(this.wizard?.registration.id);
      if (query.queryKey[0] === getRegistrationDocsKey[0]) {
        if (query.state.fetchStatus === 'fetching') this.isLoading = true;
        if (!query.state.data?.result) return;
        await this.prepareFiles(query.state.data.result);
        this.isLoading = false;
      }
    });
  }

  get isRequiredDocsFilled(): boolean {
    // requiredDocs ещё не загружены (null/undefined) — переход не разрешаем
    if (!this.requiredDocs) return false;

    const requiredDocTypes = this.requiredDocs.filter((doc) => doc.isRequired).map((doc) => doc.code);

    // OSI-756: обязательных документов нет (СФУ / смена председателя — все isRequired=false)
    // — шаг проходим без загрузки файлов. Раньше пустой список трактовался как «не заполнено»
    // и намертво блокировал кнопку «Далее».
    if (!requiredDocTypes.length) return true;

    const loadedDocTypes = this.files.map((doc) => doc.docTypeCode);

    // Проверка !loadedDocTypes.length убрана намеренно: при непустом списке обязательных
    // every по пустым загрузкам сам вернёт false — прежнее условие было избыточным.
    return requiredDocTypes.every((reqDoc) => loadedDocTypes.includes(<DocTypeCodes>reqDoc));
  }

  /**
   * Документы, которые показываем на шаге «Документы».
   *
   * OSI-756: при СФУ (CHANGE_UNION_TYPE) сканы реквизитов счетов грузятся на следующем
   * шаге «Счета» под кодом IBAN_INFO, поэтому необязательные дропзоны CURRENT_IBAN_INFO /
   * SAVING_IBAN_INFO здесь — дубль, который только путает. Скрываем их.
   *
   * Уже загруженные документы продолжаем показывать, чтобы пользователь мог их удалить
   * (заявки, начатые до этой правки).
   *
   * В остальных видах заявок необязательные документы НЕ скрываем: загрузка
   * SAVING_IBAN_INFO — единственный способ заявить сберегательный счёт, от неё зависят
   * состав шагов визарда и валидация счетов.
   */
  get visibleDocs(): ResponsesRequiredDocsResponse[] {
    if (!this.requiredDocs) return [];
    if (!this.wizard?.isChangeUnionType) return this.requiredDocs;

    return this.requiredDocs.filter(
      (doc) => doc.isRequired || this.files.some((file) => file.docTypeCode === doc.code)
    );
  }

  cleanup = () => {
    this._disposeAllReactionsController.abort();
  };

  /** Сжатие PDF идёт долго (~1 секунда на страницу) — показываем, на какой мы странице. */
  setCompressionProgress = (docTypeCode: string, page: number, total: number) => {
    this.compressionProgress = { docTypeCode, page, total };
  };

  resetCompressionProgress = () => {
    this.compressionProgress = null;
  };

  // Тип уточнён с DocScan ({id, fileName}) на AddScanDoc: сюда приходит результат
  // fileToScanDoc, и наружу уходит именно он — старая аннотация не совпадала ни с
  // фактическим аргументом, ни с телом запроса.
  uploadScan = async (scan: AddScanDoc) => {
    try {
      if (!this.wizard?.registration?.id) return;

      this.isLoading = true;

      const postScanMutation = this._mutationCache.build(queryClient, getPostApiRegistrationsIdDocsMutationOptions());

      await postScanMutation.execute({ id: this.wizard.registration.id, data: scan });

      await queryClient.refetchQueries({
        queryKey: getGetApiRegistrationsIdDocsQueryKey(this.wizard.registration.id)
      });
    } catch (e) {
      logger.error(e);
      // 413 от внешнего nginx приходит HTML-страницей: интерцептор кладёт в message
      // i18n-ключ, getFileErrorMessage его разворачивает. Иначе — прежняя общая «Ошибка».
      notifications.toast(getFileErrorMessage(e), 'error');
    } finally {
      // Интерцептор на 401 (истёкший токен) резолвит промис undefined, а НЕ бросает →
      // блок catch не входит. Без finally isLoading остаётся true и LoadingScreen висит вечно.
      this.isLoading = false;
    }
  };

  deleteDoc = async (doc: RegistrationDocFile): Promise<void> => {
    try {
      if (!this.wizard?.registration?.id) return;

      this.isLoading = true;

      const deleteMutation = this._mutationCache.build(
        queryClient,
        getDeleteApiRegistrationsIdDocsDocIdMutationOptions()
      );

      await deleteMutation.execute({ id: this.wizard.registration.id, docId: doc.id });

      await queryClient.refetchQueries({
        queryKey: getGetApiRegistrationsIdDocsQueryKey(this.wizard.registration.id)
      });
    } catch (e: unknown) {
      if (typeof e === 'string') {
        console.error(e.toString());
        notifications.error(e);
        return;
      }
      if (e instanceof Error) {
        console.error(e.message);
        notifications.error(e.message);
      }
    } finally {
      // Интерцептор на 401 (истёкший токен) резолвит промис undefined, а НЕ бросает →
      // блок catch не входит. Без finally isLoading остаётся true и LoadingScreen висит вечно.
      this.isLoading = false;
    }
  };

  private loadData = async () => {
    const registrationId = this.wizard?.registration?.id;
    if (!registrationId) return;
    try {
      this.isLoading = true;
      const requiredDocsResponse = await queryClient.fetchQuery(
        getGetApiRegistrationsReqdocsQueryOptions({ registrationId })
      );

      this.requiredDocs = requiredDocsResponse.result;
    } finally {
      this.isLoading = false;
    }
  };

  private loadFiles = async () => {
    if (!this.wizard?.registration?.id) return;
    try {
      this.isLoading = true;
      await queryClient.fetchQuery(
        getGetApiRegistrationsIdDocsQueryOptions(this.wizard.registration.id, { query: { staleTime: 0 } })
      );
    } catch (e) {
      logger.error(e);
    } finally {
      // Интерцептор на 401 (истёкший токен) резолвит промис undefined, а НЕ бросает →
      // блок catch не входит. Без finally isLoading остаётся true и LoadingScreen висит вечно.
      this.isLoading = false;
    }
  };

  private prepareFiles = async (rawDocs: DbRegistrationDoc[]) => {
    // TODO: эта функция каждый раз вызывается всё больше и больше раз, надо найти причину
    this.files = await Promise.all(rawDocs.map(async (doc) => fromDocToFile(doc, true)));
  };
}

export const token = Symbol.for('IRegistrationDocsViewModel');
